import type { VisualElement } from "@judo/model-api";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { useDataStoreOptional, useVisualPropertyOverrides } from "../contexts";
import { usePageContextOptional } from "../contexts";
import type { DataStoreState } from "../store";
import { deepEqual } from "../utils";

// No-op store for when DataProvider is not available
const EMPTY_SNAPSHOT = { hiddenByValue: undefined };

/**
 * Hook for evaluating the runtime visibility of any VisualElement.
 *
 * Resolution priority (highest to lowest):
 * 1. VisualPropertyOverrides (customization) — `hidden` field
 * 2. hiddenBy dynamic data — resolved AttributeType boolean field from DataStore
 * 3. Static model property — element.hidden
 *
 * Safe to use outside DataProvider/PageProvider — returns static hidden value when context is unavailable.
 *
 * @param element - The VisualElement to evaluate visibility for
 * @returns `true` if the element should be hidden, `false` otherwise
 */
export function useElementVisibility(element: VisualElement): boolean {
	const store = useDataStoreOptional();
	const pageContext = usePageContextOptional();
	const transferId = pageContext?.transferId ?? "";

	const hiddenByAttr = element.hiddenBy?.name;

	// Cache for selector result to ensure referential stability
	const cacheRef = useRef<{ hiddenByValue: unknown }>({ hiddenByValue: undefined });

	const selector = useCallback(
		(state: DataStoreState): { hiddenByValue: unknown } => {
			if (!hiddenByAttr) {
				return cacheRef.current;
			}
			const data = state.transfers.get(transferId)?.data;
			const newResult = { hiddenByValue: data ? data[hiddenByAttr] : undefined };

			if (deepEqual(cacheRef.current, newResult)) {
				return cacheRef.current;
			}
			cacheRef.current = newResult;
			return newResult;
		},
		[transferId, hiddenByAttr]
	);

	const subscribe = useCallback(
		(cb: () => void) => (store ? store.subscribe(selector, cb) : () => {}),
		[store, selector]
	);
	const getSnapshot = useCallback(() => (store ? store.getSnapshot(selector) : EMPTY_SNAPSHOT), [store, selector]);

	const { hiddenByValue } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	// Resolve hidden: static → *By dynamic → customization override
	const toBoolean = (value: unknown, defaultValue: boolean): boolean => {
		if (typeof value === "boolean") return value;
		if (value === "true") return true;
		if (value === "false") return false;
		return defaultValue;
	};

	let hidden = toBoolean((element as unknown as Record<string, unknown>).hidden, false);

	// hiddenBy: if referenced boolean field is true → override to hidden
	if (hiddenByAttr && toBoolean(hiddenByValue, false)) {
		hidden = true;
	}

	// Customization override wins over everything
	const propertyOverrides = useVisualPropertyOverrides(element);
	if (propertyOverrides?.hidden != null) {
		return propertyOverrides.hidden;
	}

	return hidden;
}
